import { useEffect } from "react";

const NAV_TARGETS = {
  home: ["home"],
  services: ["services", "service"],
  workflows: ["workflows", "workflow"],
  solutions: ["business-tools", "solutions", "solution", "products", "product"],
  contact: ["contact", "contacto"],
};

function clean(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function compact(value = "") {
  return clean(value).replace(/[^a-z0-9]/g, "");
}

function getElementText(element) {
  if (!element) return "";

  return clean(
    [
      element.textContent,
      element.innerText,
      element.getAttribute?.("href"),
      element.getAttribute?.("to"),
      element.getAttribute?.("aria-label"),
      element.getAttribute?.("data-target"),
      element.getAttribute?.("data-section"),
    ]
      .filter(Boolean)
      .join(" ")
  );
}

function getNavKey(element) {
  const text = compact(getElementText(element));

  if (!text) return null;

  if (text.includes("talktoaiassistant")) return null;

  if (text === "home" || text.includes("#home")) return "home";
  if (text.includes("services") || text.includes("service")) return "services";
  if (text.includes("workflows") || text.includes("workflow")) return "workflows";
  if (text.includes("solutions") || text.includes("solution")) return "solutions";
  if (text.includes("contact") || text.includes("contacto")) return "contact";

  return null;
}

function findSectionByKeyword(keywords) {
  for (const keyword of keywords) {
    const direct = document.getElementById(keyword);

    if (direct) return direct;
  }

  const sections = Array.from(
    document.querySelectorAll("section, main > div, main section, [data-section]")
  );

  for (const keyword of keywords) {
    const found = sections.find((section) => {
      const id = compact(section.id || "");
      const className = compact(section.className || "");
      const dataSection = compact(section.getAttribute?.("data-section") || "");

      return (
        id.includes(keyword) ||
        className.includes(keyword) ||
        dataSection.includes(keyword)
      );
    });

    if (found) return found;
  }

  for (const keyword of keywords) {
    const heading = Array.from(
      document.querySelectorAll("section h1, section h2, section h3, main h1, main h2, main h3")
    ).find((item) => compact(item.textContent || "").includes(keyword));

    if (heading) {
      return heading.closest("section") || heading.closest("div") || heading;
    }
  }

  return null;
}

function scrollToTarget(key) {
  if (key === "home") {
    window.history.replaceState(null, "", window.location.pathname);
    window.scrollTo({ top: 0, behavior: "smooth" });
    return true;
  }

  const target = findSectionByKeyword(NAV_TARGETS[key] || []);

  if (!target) {
    console.warn(`[FIKAMI navigation] Target not found for: ${key}`);
    return false;
  }

  const offset = 90;
  const top = target.getBoundingClientRect().top + window.scrollY - offset;

  window.history.replaceState(null, "", `#${key}`);
  window.scrollTo({ top, behavior: "smooth" });

  return true;
}

function prepareSectionIds() {
  const mappings = [
    { id: "services", keywords: ["services", "service"] },
    { id: "workflows", keywords: ["workflows", "workflow"] },
    { id: "solutions", keywords: ["business-tools", "solutions", "solution", "products", "product"] },
    { id: "contact", keywords: ["contact", "contacto"] },
  ];

  mappings.forEach(({ id, keywords }) => {
    if (document.getElementById(id)) return;

    const target = findSectionByKeyword(keywords);

    if (!target) return;

    if (!target.id) {
      target.id = id;
      return;
    }

    const anchor = document.createElement("span");
    anchor.id = id;
    anchor.setAttribute("aria-hidden", "true");
    anchor.style.position = "absolute";
    anchor.style.marginTop = "-90px";
    anchor.style.pointerEvents = "none";
    target.prepend(anchor);
  });
}

function patchVisibleNavItems() {
  const items = Array.from(
    document.querySelectorAll("nav a, nav button, header a, header button")
  );

  items.forEach((item) => {
    const key = getNavKey(item);

    if (!key) return;

    item.setAttribute("data-fikami-nav", key);

    if (item.tagName.toLowerCase() === "a") {
      item.setAttribute("href", key === "home" ? "#home" : `#${key}`);
    }
  });
}

export default function SiteNavigationFix() {
  useEffect(() => {
    prepareSectionIds();
    patchVisibleNavItems();

    const observer = new MutationObserver(() => {
      prepareSectionIds();
      patchVisibleNavItems();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const handleClick = (event) => {
      const path = event.composedPath ? event.composedPath() : [];
      const trigger =
        path.find?.(
          (node) =>
            node &&
            node.nodeType === 1 &&
            typeof node.matches === "function" &&
            node.matches("a, button, [role='button'], [data-fikami-nav]")
        ) || event.target.closest?.("a, button, [role='button'], [data-fikami-nav]");

      if (!trigger) return;

      const key = trigger.getAttribute?.("data-fikami-nav") || getNavKey(trigger);

      if (!key) return;

      const didScroll = scrollToTarget(key);

      if (didScroll) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener("click", handleClick, true);

    if (window.location.hash) {
      const hashKey = compact(window.location.hash);

      const key =
        hashKey.includes("workflows")
          ? "workflows"
          : hashKey.includes("solutions")
            ? "solutions"
            : hashKey.includes("services")
              ? "services"
              : hashKey.includes("contact")
                ? "contact"
                : hashKey.includes("home")
                  ? "home"
                  : null;

      if (key) {
        setTimeout(() => scrollToTarget(key), 300);
      }
    }

    return () => {
      observer.disconnect();
      document.removeEventListener("click", handleClick, true);
    };
  }, []);

  return null;
}
